import { PageWrapper } from '@/components/layout/PageWrapper';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertTriangle, CheckCircle, XCircle, Clock, Activity } from 'lucide-react';
import { RoleProtectedRoute } from '@/components/auth/RoleProtectedRoute';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export default function SecurityMonitoring() {
  // Placeholder data until migration is applied
  const accessDenials: any[] = [];
  const roleChanges: any[] = [];
  const loadingDenials = false;
  const loadingRoles = false;

  return (
    <RoleProtectedRoute requiredRoles={['super_admin', 'admin']}>
      <PageWrapper>
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Shield className="w-8 h-8 text-primary" />
              المراقبة الأمنية
            </h1>
            <p className="text-muted-foreground mt-2">
              مراقبة محاولات الوصول غير المصرح بها وتغييرات الأدوار
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  محاولات مرفوضة اليوم
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {accessDenials?.filter(d => {
                    const today = new Date().toDateString();
                    return new Date(d.attempted_at).toDateString() === today;
                  }).length || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-600" />
                  تغييرات الأدوار اليوم
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {roleChanges?.filter(r => {
                    const today = new Date().toDateString();
                    return new Date(r.created_at).toDateString() === today;
                  }).length || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  محاولات مشبوهة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {accessDenials?.filter(d => d.denial_reason?.includes('privilege')).length || 0}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  حالة النظام
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  آمن
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Notice */}
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-blue-800 text-sm">
                معلومة
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-blue-700">
              سيتم تفعيل سجلات الأمان بالكامل بعد تطبيق Database Migration. 
              حالياً، النظام يعتمد على RLS policies الأساسية.
            </CardContent>
          </Card>

          {/* Access Denials */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-600" />
                محاولات الوصول المرفوضة
              </CardTitle>
              <CardDescription>
                سجل المحاولات غير الناجحة للوصول إلى الموارد
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingDenials ? (
                <div className="text-center py-8">جاري التحميل...</div>
              ) : accessDenials && accessDenials.length > 0 ? (
                <div className="space-y-2">
                  {accessDenials.map((denial) => (
                    <div
                      key={denial.id}
                      className="p-4 rounded-lg border border-red-200 bg-red-50"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-red-900">
                            {denial.denial_reason || 'محاولة وصول غير مصرح بها'}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            المورد: {denial.resource_type} - {denial.resource_id}
                          </p>
                          <p className="text-xs text-red-600 mt-1">
                            <Clock className="w-3 h-3 inline ml-1" />
                            {new Date(denial.attempted_at).toLocaleString('ar-SA')}
                          </p>
                        </div>
                        <Badge variant="destructive">مرفوض</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  لا توجد محاولات وصول مرفوضة
                </div>
              )}
            </CardContent>
          </Card>

          {/* Role Changes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                سجل تغييرات الأدوار
              </CardTitle>
              <CardDescription>
                تتبع جميع التغييرات على أدوار المستخدمين
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingRoles ? (
                <div className="text-center py-8">جاري التحميل...</div>
              ) : roleChanges && roleChanges.length > 0 ? (
                <div className="space-y-2">
                  {roleChanges.map((change) => (
                    <div
                      key={change.id}
                      className="p-4 rounded-lg border"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium">
                            {change.action === 'assigned' ? 'تم تعيين' : 
                             change.action === 'removed' ? 'تم إزالة' : 
                             change.action === 'expired' ? 'انتهت صلاحية' : change.action}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            الدور: {change.role}
                          </p>
                          {change.reason && (
                            <p className="text-xs text-muted-foreground mt-1">
                              السبب: {change.reason}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            <Clock className="w-3 h-3 inline ml-1" />
                            {new Date(change.created_at).toLocaleString('ar-SA')}
                          </p>
                        </div>
                        <Badge 
                          variant={change.action === 'assigned' ? 'default' : 'secondary'}
                        >
                          {change.action}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  لا توجد تغييرات على الأدوار
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </PageWrapper>
    </RoleProtectedRoute>
  );
}
